import { useEffect, useState } from 'react';
import { fetchTeam } from '../lib/api';
import type { TeamResponse, PublicTeamMember } from '../lib/types';
import TeamCard from '../components/TeamCard';
import { PageHero, Stars } from '../components/SiteChrome';

export default function TeamPage() {
  const [data, setData] = useState<TeamResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTeam().then(setData).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
  }, []);

  // All field inspectors in one group — network owner-operators and full-time
  // staff together, equal footing. Management lives in its own support section.
  const inspectors = data ? [...data.network, ...data.inspector].sort(byOrder) : [];

  return (
    <>
      <PageHero
        eyebrow="Our Team"
        title="Meet the Inspect Canada inspectors"
        subtitle="Independent owner-operators and full-time staff inspectors — every inspection held to the same Inspect Canada standard, report, and buy-back guarantee."
        breadcrumb={<><a href="https://inspect.ca" className="hover:text-white">Home</a> <span className="mx-1">/</span> <span className="text-white">Our Team</span></>}
      />

      <ReviewsTrustStrip />

      {error ? (
        <p className="max-w-7xl mx-auto px-5 py-16 text-slate-500">
          Couldn't load the team right now. <span className="text-xs text-slate-400">({error})</span>
        </p>
      ) : !data ? (
        <p className="max-w-7xl mx-auto px-5 py-16 text-slate-500">Loading…</p>
      ) : (
        <main className="max-w-7xl mx-auto px-5 py-16 space-y-16">
          <p className="text-center max-w-3xl mx-auto text-slate-600 text-base leading-relaxed">
            <strong className="text-navy">One standard on every inspection.</strong> Some of our inspectors
            run their own businesses powered by Inspect Canada; others are full-time staff. Either way you get
            the identical Inspect Canada checklist, comprehensive report, and buy-back guarantee.
          </p>

          <Section
            title="Our Inspectors"
            subtitle="Book directly with any inspector below — each one carries the full Inspect Canada standard."
          >
            <Grid members={inspectors} />
          </Section>

          {data.management.length ? (
            <Section
              title="Team & Support"
              subtitle="The crew behind every inspection — scheduling, support, and operations."
            >
              <Grid members={data.management} support />
            </Section>
          ) : null}
        </main>
      )}
    </>
  );
}

function byOrder(a: PublicTeamMember, b: PublicTeamMember) {
  const ao = a.publicOrder ?? 999;
  const bo = b.publicOrder ?? 999;
  if (ao !== bo) return ao - bo;
  return (a.lastName ?? '').localeCompare(b.lastName ?? '');
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="text-center max-w-2xl mx-auto mb-8">
        <h2 className="text-3xl font-extrabold text-navy">{title}</h2>
        {subtitle ? <p className="mt-2 text-slate-600">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Grid({ members, support }: { members: PublicTeamMember[]; support?: boolean }) {
  if (!members.length) return <p className="text-slate-400 text-sm text-center">Roster is being updated.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {members.map((m) => <TeamCard key={m.id} member={m} support={support} />)}
    </div>
  );
}

// The brand's #1 proof point — mirrors the "Highest Rated in Canada" banner on
// the live homepage.
function ReviewsTrustStrip() {
  return (
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-center">
        <div className="flex items-center gap-2">
          <Stars className="text-lg" />
          <span className="font-extrabold text-navy">Highest Rated Home Inspector in Canada</span>
        </div>
        <span className="hidden sm:inline text-slate-300">|</span>
        <span className="text-slate-600">
          <strong className="text-navy">3,800+</strong> verified 5-star Google reviews
        </span>
        <a href="https://www.google.com/search?q=inspect+canada+reviews" target="_blank" rel="noreferrer"
           className="text-accent font-semibold text-sm hover:underline">
          Read the reviews →
        </a>
      </div>
    </div>
  );
}
